/*
  A/B Test: [Nav] Text-based mobile menu — VARIANT (B) behaviour.

  Deliberately small. The drawers are Focal's <mobile-navigation> and
  <predictive-search-drawer> elements and the accordions are Focal's
  toggle-button / collapsible-content, so open, close, focus trapping,
  aria-expanded and predictive search itself are all already handled. This file
  only covers what markup and CSS cannot do on their own:

    1. Measure where the site header ends, so both variant panels can start
       below it (the design keeps cart + account reachable while they are open,
       and puts each panel's close control in the header row).
    2. Flag when the menu's search has a query, so the results can take over the
       drawer and the nav list can step aside.
    3. Load the Intelligems reveal stylesheet for ?tmm=b dev/QA previews.

  Teardown: delete every tmm-* file, then grep "tmm-".
*/

(function () {
  'use strict';

  var MENU_ID = 'tmm-menu-drawer';
  var MENU_SEARCH_ID = 'tmm-menu-search';
  var PANEL_IDS = [MENU_ID, 'tmm-search-drawer'];

  /* ---------- 1. Panel top offset ----------
     Focal's .drawer is fixed to top:0. The variant panels start at the header's
     bottom edge instead, which moves with scroll position: the announcement bar
     is non-sticky and scrolls away while the header itself is sticky, so the
     offset shrinks once the page has scrolled. Written to :root rather than to
     one panel because both panels and both overlays read it, and the menu drawer
     is relocated to <body> at upgrade time (append-body) so it shares no
     ancestor with the header.

     Measuring as a panel opens is enough, because Focal locks body scroll for
     as long as one is open. */

  function setPanelTop() {
    var header = document.querySelector('.shopify-section--header');
    var top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;

    document.documentElement.style.setProperty('--tmm-drawer-top', Math.round(top) + 'px');
  }

  function initPanelOffset() {
    var panels = PANEL_IDS.map(function (id) {
      return document.getElementById(id);
    }).filter(Boolean);

    if (!panels.length) return;

    setPanelTop();

    panels.forEach(function (panel) {
      // A panel carries `open` while visible; re-measure as it opens so the
      // offset reflects the scroll position it was opened from.
      new MutationObserver(function () {
        if (panel.hasAttribute('open')) setPanelTop();
      }).observe(panel, { attributes: true, attributeFilter: ['open'] });
    });

    window.addEventListener('resize', setPanelTop);
  }

  /* ---------- 2. Menu search state ----------
     Focal's predictive search fills its own results container but has no notion
     of the menu around it, so nothing else can tell the nav list to get out of
     the way. A class on the drawer does that, mirroring how the control's own
     inline search works (xtis_filled in xt-custom.css). */

  function initMenuSearchState() {
    var drawer = document.getElementById(MENU_ID);
    var search = document.getElementById(MENU_SEARCH_ID);

    if (!drawer || !search) return;

    var input = search.querySelector('[name="q"]');
    if (!input) return;

    function sync() {
      drawer.classList.toggle('tmm-searching', input.value.trim() !== '');
    }

    input.addEventListener('input', sync);

    // "Cancel" (and the no-results "New search") clear the field through
    // Focal's own handler, which does not dispatch an input event — so read the
    // field back on the next frame instead of trusting one.
    search.addEventListener('click', function (event) {
      if (event.target.closest('[data-action="reset-search"]')) {
        requestAnimationFrame(sync);
      }
    });

    // Reopening the menu should land on the nav, not on stale results.
    //
    // Clearing the value alone is not enough: Focal only resets its results,
    // footer and loading state from an input event, so without one the old
    // results stay rendered while .tmm-searching is gone — they fill the
    // flex: 0 0 auto search block, shove the nav out of view and nothing in
    // the drawer can be tapped or scrolled. Dispatched the way Focal's own
    // "New search" does it, which also runs sync through the listener above.
    new MutationObserver(function () {
      if (drawer.hasAttribute('open') || input.value === '') return;

      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });

    sync();
  }

  /* ---------- 3. Dev/QA preview of the B group (?tmm=b) ----------
     Loads tmm-reveal.css — the exact stylesheet the Intelligems variant group
     injects — so the preview can never drift from the live test. */

  function initPreview() {
    if (!/[?&]tmm=b\b/.test(window.location.search)) return;
    if (document.querySelector('link[data-tmm-reveal]')) return;

    var base = document.querySelector('link[href*="tmm-nav.css"]');
    if (!base) return;

    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = base.href.replace('tmm-nav.css', 'tmm-reveal.css');
    link.setAttribute('data-tmm-reveal', '');
    document.head.appendChild(link);
  }

  function init() {
    initPreview();
    initPanelOffset();
    initMenuSearchState();
  }

  // `append-body` relocates the menu drawer to <body>, so wait for a parsed DOM
  // before looking the panels up by id.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
